<template>
  <!-- The chip has room for the label and nothing else, which is exactly a
       PrimeVue Tag: value + icon + severity. The brand preset already maps
       `warn`/`danger` onto the status palette, so only density is set here.
       No `role` and no live region: a badge renders once per card in a
       scrolling list, so announcing each one announces the list. -->
  <!-- NOT the `sale-status-banner` base class: it carries the block layout
       (display:flex, gap:10px) at a higher specificity than `.p-tag`, which
       would override Tag's own inline-flex and gap. -->
  <Tag
    v-if="arrangement === 'chip'"
    class="sale-status-banner--chip"
    :value="saleStatus.label"
    :icon="icon"
    :severity="state === 'paused' ? 'warn' : 'danger'"
    :dt="chipTokens"
  />

  <span
    v-else
    class="sale-status-banner"
    :class="[`sale-status-banner--${arrangement}`, `sale-status-banner--${state}`]"
    role="status"
  >
    <!-- Decorative: the glyph repeats the state the label beside it already
         names, so exposing it would announce the state twice. -->
    <i :class="icon" aria-hidden="true" />

    <span v-if="arrangement === 'stacked'" class="sale-status-banner__text">
      <span
        v-if="saleStatus.label"
        class="sale-status-banner__label sale-status-banner__label--stacked"
      >{{ saleStatus.label }}</span>
      <span
        v-if="saleStatus.line"
        class="sale-status-banner__line"
      >{{ saleStatus.line }}<slot /></span>
      <!-- The clerk's own words for this pause, when they gave any. -->
      <span v-if="saleStatus.reason" class="sale-status-banner__reason">“{{ saleStatus.reason }}”</span>
    </span>

    <span v-else class="sale-status-banner__text">
      <!-- The separator belongs to the PAIR, not to either half: a status
           object missing one of them used to render a lone middle dot. -->
      <b v-if="saleStatus.label" class="sale-status-banner__label">{{ saleStatus.label }}</b>
      <span v-if="saleStatus.label && saleStatus.line" class="sale-status-banner__separator"> · </span>
      <span
        v-if="saleStatus.line"
        class="sale-status-banner__line"
      >{{ saleStatus.line }}<slot /></span>
      <span v-if="saleStatus.reason" class="sale-status-banner__reason sale-status-banner__reason--block">“{{ saleStatus.reason }}”</span>
    </span>
  </span>
</template>

<script>
// The three shapes this component draws, read TWICE — once by the `variant`
// validator, which only warns, and once by the clamp that decides what actually
// renders. A plain `<script>` block rather than `<script setup>` because
// `defineProps` is lifted out of the setup body and cannot see a local: module
// scope survives the lift, and a set read from both sides should not be written
// out twice. See *The Component Contract* in vue-coding-standards.md.
const ARRANGEMENTS = Object.freeze(['banner', 'stacked', 'chip']);
</script>

<script setup>
import { computed } from 'vue';
import Tag from 'primevue/tag';

// Why bidding is unavailable: the sale behind this asset is paused by the clerk
// or finished. Several surfaces drew this independently and all of them read
// the same status shape, so only the arrangement ever differed. Those
// arrangements are the `variant` prop; the amber/red split and the pause/flag
// glyph are the component's.
//
// The `chip` variant IS a PrimeVue Tag — value + icon + severity is exactly its
// API, and the brand preset maps `warn`/`danger` onto the status palette as
// solid fills. Only density is overridden, per instance via `dt`, because a
// state badge is 8px and an ordinary Tag is not; putting that in the preset
// would define every future Tag by this one niche use.
//
// The `banner` and `stacked` variants stay hand-rolled. They are multi-line
// explanatory blocks with a reason line and a slot — closer to a Message than a
// Tag, and Tag has room for neither.
//
// Purely presentational: the caller derives `saleStatus` (null while the sale is
// running) and decides what the banner replaces.
//
// SLOTS
//   default — trailing note appended to the line, for surfaces that offer a way
//             forward ("— set a proxy to bid on resume."). Both blocks render
//             it; the chip has no room and ignores it.
defineOptions({ name: 'SaleStatusBanner' });

const props = defineProps({
  // { state: 'paused' | 'finished', label, line, reason }. `label` and `line`
  // are pre-formatted display strings; `reason` is the clerk's free text and
  // may be absent.
  //
  // The validator reaches inside the object, which the `type` declaration
  // cannot: `{}` satisfies `type: Object, required: true` in full, and used to
  // render an unstyled block holding a lone middle dot. It checks the three
  // fields the component renders unconditionally and leaves `reason` alone,
  // which is documented as optional.
  saleStatus: {
    type: Object,
    required: true,
    validator: (value) => (
      ['paused', 'finished'].includes(value.state)
      && typeof value.label === 'string' && value.label.length > 0
      && typeof value.line === 'string' && value.line.length > 0
    ),
  },
  // 'banner'  — label · line on one line, for a tray or dock
  // 'stacked' — label over line, for a lane block with room to breathe
  // 'chip'    — label only, solid fill, for a compact card
  variant: {
    type: String,
    default: 'banner',
    validator: (value) => ARRANGEMENTS.includes(value),
  },
});

// A `validator` warns in development and is compiled out of production, so it
// can tell the call site it is wrong and cannot decide what ships. These two
// clamps decide. They exist so the readers of each value AGREE: the glyph, the
// badge's severity and the modifier class used to be three independent
// expressions, and an unrecognized state read as "finished" on the badge and as
// nothing at all on the blocks, whose state rules matched no class.
const state = computed(() => (props.saleStatus.state === 'paused' ? 'paused' : 'finished'));
const arrangement = computed(() => (ARRANGEMENTS.includes(props.variant) ? props.variant : 'banner'));

const icon = computed(() => (state.value === 'paused' ? 'pi pi-pause' : 'pi pi-flag'));

// Scoped Tag tokens: the badge density. Small enough that Aura's own scale
// reads as a button rather than a state badge.
const chipTokens = {
  fontSize: '8px',
  fontWeight: '800',
  padding: '2px 6px',
  gap: '3px',
  borderRadius: '6px',
  icon: { size: '8px' },
};
</script>

<style scoped>
.sale-status-banner {
  display: flex;
  align-items: center;
  gap: var(--p-sale-status-banner-gap);
  /* THE WORDS ARE NEUTRAL, AND THE STATE IS THE FILL, THE BORDER AND THE GLYPH.
     A tint of a status color under that same status color as ink has a ceiling
     it cannot clear — the paused pairing measured 4.15:1 against a 4.5:1 floor
     — so the text reads the neutral ink and the color moves to the glyph. */
  color: var(--p-sale-status-banner-color);
}

.sale-status-banner i {
  flex-shrink: 0;
}

.sale-status-banner__text {
  min-width: 0;
}

.sale-status-banner__reason {
  font-style: italic;
  color: var(--p-sale-status-banner-reason-color);
  overflow-wrap: anywhere;
}

.sale-status-banner__reason--block {
  display: block;
  margin-top: 3px;
}

/* ── banner: the tray / dock arrangement ──────────────────────────────── */
.sale-status-banner--banner {
  min-height: 64px;
  padding: var(--p-sale-status-banner-banner-padding);
  border-radius: var(--p-sale-status-banner-banner-border-radius);
  font-size: var(--p-sale-status-banner-banner-font-size);
  line-height: var(--p-sale-status-banner-banner-line-height);
}

.sale-status-banner--banner i {
  font-size: var(--p-sale-status-banner-banner-icon-font-size);
}

/* ── stacked: the lane block ──────────────────────────────────────────── */
.sale-status-banner--stacked {
  padding: var(--p-sale-status-banner-stacked-padding);
  border-radius: var(--p-sale-status-banner-stacked-border-radius);
}

.sale-status-banner--stacked i {
  font-size: var(--p-sale-status-banner-stacked-icon-font-size);
}

.sale-status-banner--stacked .sale-status-banner__text {
  display: flex;
  flex-direction: column;
}

.sale-status-banner__label--stacked {
  font-size: var(--p-sale-status-banner-label-stacked-font-size);
  font-weight: var(--p-sale-status-banner-label-stacked-font-weight);
  letter-spacing: var(--p-sale-status-banner-label-stacked-letter-spacing);
  text-transform: uppercase;
}

.sale-status-banner--stacked .sale-status-banner__line {
  font-size: var(--p-sale-status-banner-stacked-line-font-size);
  color: var(--p-sale-status-banner-stacked-line-color);
  margin-top: 1px;
}

.sale-status-banner--stacked .sale-status-banner__reason {
  font-size: var(--p-sale-status-banner-stacked-reason-font-size);
  margin-top: 3px;
}

/* ── chip: the compact card badge ─────────────────────────────────────────
   Tag brings its own layout, fill and radius (see chipTokens + the preset's
   components.tag block). Only the tracking is ours — Tag has no token for it,
   and an 8px uppercase label needs the extra letter-spacing to stay legible. */
.sale-status-banner--chip {
  letter-spacing: var(--p-sale-status-banner-chip-letter-spacing);
  white-space: nowrap;
}

/* ── state: warning = paused, danger = finished ───────────────────────────
   The semantic status tokens, not the amber/red primitive ramps. Declared one
   level up from the arrangements because BOTH blocks read them — they used to
   be named for the banner while the stacked block read them anyway.

   The color lands on the glyph rather than on the block, which is what leaves
   the words on the neutral ink above. axe never judged the glyph either way —
   an icon font has no text node — and against the tint it sits at 4.15:1,
   which clears the 3:1 that non-text content is held to. */
.sale-status-banner--banner.sale-status-banner--paused,
.sale-status-banner--stacked.sale-status-banner--paused {
  background: var(--p-sale-status-banner-paused-background);
  border: var(--p-sale-status-banner-paused-border-width) solid var(--p-sale-status-banner-paused-border-color);
}

.sale-status-banner--banner.sale-status-banner--paused i,
.sale-status-banner--stacked.sale-status-banner--paused i {
  color: var(--p-sale-status-banner-paused-icon-color);
}

.sale-status-banner--banner.sale-status-banner--finished,
.sale-status-banner--stacked.sale-status-banner--finished {
  background: var(--p-sale-status-banner-finished-background);
  border: var(--p-sale-status-banner-finished-border-width) solid var(--p-sale-status-banner-finished-border-color);
}

.sale-status-banner--banner.sale-status-banner--finished i,
.sale-status-banner--stacked.sale-status-banner--finished i {
  color: var(--p-sale-status-banner-finished-icon-color);
}

/* The chip's own fill comes from the preset — components.tag maps warn/danger
   onto the same status palette, as a SOLID fill rather than Aura's translucent
   tint, because a state badge this small has to carry at a glance. */
</style>
